<template>
  <Dialog
    :header="title"
    :visible="display"
    :style="{ width: '70vw' }"
    :modal="true"
    :closeOnEscape="false"
    :contentStyle="{ overflow: 'visible' }"
    closable
    @update:visible="CloseModal"
  >
    <div class="p-p-4" v-if="mode === 'simulate'">
      <div class="p-fluid p-grid">
        <div class="p-field p-col-12 p-md-3">
          <span class="p-float-label">
            <InputText id="calcSymbolAggregate" type="text" :modelValue="form.symbolAggregate" @update:modelValue="SetUpperCase('symbolAggregate', $event)" />
            <label for="calcSymbolAggregate">Underlying Asset</label>
          </span>
        </div>
        <div class="p-field p-col-12 p-md-3">
          <span class="p-float-label">
            <InputText id="calcSymbol" type="text" :modelValue="form.symbol" @update:modelValue="SetUpperCase('symbol', $event)" />
            <label for="calcSymbol">Option Symbol</label>
          </span>
        </div>
        <div class="p-field p-col-12 p-md-3">
          <Dropdown v-model="form.type" :options="types" optionValue="value" optionLabel="name" placeholder="Type" />
        </div>
        <div class="p-field p-col-12 p-md-3">
          <Dropdown v-model="form.action" :options="actions" optionValue="value" optionLabel="name" placeholder="Action" />
        </div>
        <div class="p-field p-col-12 p-md-3">
          <span class="p-float-label">
            <InputNumber id="calcSpot" v-model="form.spot" mode="currency" currency="USD" locale="en-US" :minFractionDigits="2" :maxFractionDigits="4" />
            <label for="calcSpot">Spot Price</label>
          </span>
        </div>
        <div class="p-field p-col-12 p-md-3">
          <span class="p-float-label">
            <InputNumber id="calcStrike" v-model="form.strike" mode="currency" currency="USD" locale="en-US" :minFractionDigits="2" :maxFractionDigits="4" />
            <label for="calcStrike">Strike Price</label>
          </span>
        </div>
        <div class="p-field p-col-12 p-md-3">
          <span class="p-float-label">
            <InputNumber id="calcPremium" v-model="form.premium" mode="currency" currency="USD" locale="en-US" :minFractionDigits="2" :maxFractionDigits="4" />
            <label for="calcPremium">Premium</label>
          </span>
        </div>
        <div class="p-field p-col-12 p-md-3">
          <span class="p-float-label">
            <InputNumber id="calcQuantity" v-model="form.quantity" mode="decimal" :maxFractionDigits="0" />
            <label for="calcQuantity">Quantity</label>
          </span>
        </div>
        <div class="p-field p-col-12 p-md-3">
          <span class="p-float-label">
            <InputNumber id="calcCosts" v-model="form.costs" mode="currency" currency="USD" locale="en-US" />
            <label for="calcCosts">Costs</label>
          </span>
        </div>
        <div class="p-field p-col-12 p-md-3">
          <span class="p-float-label">
            <Calendar id="calcExpiration" v-model="form.expirationDate" dateFormat="dd/mm/yy" :showIcon="true" />
            <label for="calcExpiration">Expiration Date</label>
          </span>
        </div>
        <div class="p-field p-col-12 p-md-3">
          <span class="p-float-label">
            <Calendar id="calcBaseDate" v-model="form.baseDate" dateFormat="dd/mm/yy" :showIcon="true" />
            <label for="calcBaseDate">Base Date</label>
          </span>
        </div>
        <div class="p-field p-col-12 p-md-3">
          <span class="p-float-label">
            <InputNumber id="calcCdi" v-model="form.cdiPercent" mode="decimal" suffix=" %" :minFractionDigits="2" :maxFractionDigits="4" />
            <label for="calcCdi">Annual CDI / Rate</label>
          </span>
        </div>
      </div>

      <ul v-if="!result.valid" class="p-text-left text-danger">
        <li v-for="(error, index) in result.errors" :key="index">{{ error }}</li>
      </ul>

      <div v-else class="p-grid">
        <div class="p-col-12 p-md-6 p-lg-4" v-for="metric in metrics" :key="metric.label">
          <div class="p-text-bold">{{ metric.label }}</div>
          <div>{{ metric.value }}</div>
        </div>
      </div>
    </div>

    <div class="p-p-4" v-else-if="!trackable">
      <p class="p-text-left text-danger">This order does not have the required data for tracking (option, strike, expiration, price, and quantity).</p>
    </div>

    <div class="p-p-4" v-else>
      <div class="p-grid p-text-left p-mb-3">
        <div class="p-col-12 p-md-6 p-lg-3" v-for="item in orderSummary" :key="item.label">
          <div class="p-text-bold">{{ item.label }}</div>
          <div>{{ item.value }}</div>
        </div>
      </div>

      <div class="p-fluid p-grid">
        <div class="p-field p-col-12 p-md-3">
          <span class="p-float-label">
            <InputNumber id="trackSpot" v-model="tracking.currentSpot" mode="currency" currency="USD" locale="en-US" :minFractionDigits="2" :maxFractionDigits="4" />
            <label for="trackSpot">Current Spot Price</label>
          </span>
        </div>
        <div class="p-field p-col-12 p-md-3">
          <span class="p-float-label">
            <InputNumber id="trackPremium" v-model="tracking.currentPremium" mode="currency" currency="USD" locale="en-US" :minFractionDigits="2" :maxFractionDigits="4" />
            <label for="trackPremium">Current Premium</label>
          </span>
        </div>
        <div class="p-field p-col-12 p-md-2">
          <span class="p-float-label">
            <Calendar id="trackBaseDate" v-model="tracking.baseDate" dateFormat="dd/mm/yy" :showIcon="true" />
            <label for="trackBaseDate">Base Date</label>
          </span>
        </div>
        <div class="p-field p-col-12 p-md-2">
          <span class="p-float-label">
            <InputNumber id="trackCdi" v-model="tracking.cdiPercent" mode="decimal" suffix=" %" :minFractionDigits="2" :maxFractionDigits="4" />
            <label for="trackCdi">Annual CDI</label>
          </span>
        </div>
        <div class="p-field p-col-12 p-md-2">
          <span class="p-float-label">
            <InputNumber id="trackOpenSpot" v-model="tracking.openSpot" mode="currency" currency="USD" locale="en-US" :minFractionDigits="2" :maxFractionDigits="4" />
            <label for="trackOpenSpot">Opening Spot (Optional)</label>
          </span>
        </div>
      </div>

      <ul v-if="!trackResult.valid" class="p-text-left text-danger">
        <li v-for="(error, index) in trackResult.errors" :key="index">{{ error }}</li>
      </ul>

      <div v-else>
        <div v-if="trackResult.captureAlert" class="p-text-left p-mb-3">
          <Tag severity="warning" icon="pi pi-exclamation-triangle" value="≥ 80% of premium captured — consider closing/buyback" />
        </div>

        <div class="p-grid">
          <div class="p-col-12 p-md-6 p-lg-4" v-for="metric in trackMetrics" :key="metric.label">
            <div class="p-text-bold">{{ metric.label }}</div>
            <div :class="metric.warn ? 'text-danger' : ''">{{ metric.value }}</div>
          </div>
        </div>

        <div class="p-text-left p-mt-3">
          <div class="p-text-bold p-mb-2">Term Progress</div>
          <ProgressBar :value="progressPercent" />
        </div>
      </div>
    </div>

    <small class="p-d-block p-mt-3">Term in business days Mon–Fri (base 252, excludes holidays)</small>

    <template #footer>
      <div class="p-d-flex p-jc-between p-ai-center w-100">
        <div>
          <Button
            v-if="mode === 'simulate'"
            :label="savedCalculationsCount > 0 ? `Saved Calculations (${savedCalculationsCount})` : 'Saved Calculations'"
            icon="pi pi-folder-open"
            class="p-button-outlined p-button-info"
            @click="showSavedDialog = true"
          />
        </div>
        <div>
          <Button label="Cancel" icon="pi pi-times" class="p-button-secondary p-mr-2" @click="Cancel" />
          <Button
            v-if="mode === 'simulate'"
            label="Save Calculation"
            icon="pi pi-save"
            class="p-button-primary p-mr-2"
            :disabled="!result.valid"
            @click="SaveCalculation"
          />
          <Button
            v-if="mode === 'simulate'"
            label="Fill Order"
            icon="pi pi-file-edit"
            class="p-button-success"
            :disabled="!result.valid"
            @click="FillOrder"
          />
          <Button
            v-else
            label="Generate Closing Order"
            icon="pi pi-replay"
            class="p-button-success"
            :disabled="!canClose"
            @click="FillCloseOrder"
          />
        </div>
      </div>
    </template>
  </Dialog>

  <!-- Dialog for viewing and loading saved calculations -->
  <Dialog
    header="Saved Calculations"
    v-model:visible="showSavedDialog"
    :style="{ width: '65vw' }"
    :modal="true"
    closable
  >
    <div v-if="savedCalculations.length === 0" class="p-p-4 p-text-center">
      <p>No saved calculations found.</p>
    </div>
    <DataTable :value="savedCalculations" class="p-datatable-sm p-datatable-striped" v-else responsiveLayout="scroll">
      <Column field="savedAt" header="Date">
        <template #body="slotProps">
          {{ $formatDate(slotProps.data.savedAt) }}
        </template>
      </Column>
      <Column header="Option">
        <template #body="slotProps">
          {{ slotProps.data.form.symbol || 'N/A' }}
          <span class="small" v-if="slotProps.data.form.symbolAggregate"> ({{ slotProps.data.form.symbolAggregate }})</span>
        </template>
      </Column>
      <Column header="Side">
        <template #body="slotProps">
          {{ slotProps.data.form.action === 0 ? 'Buy' : 'Sell' }} {{ slotProps.data.form.type === 2 ? 'Call' : 'Put' }}
        </template>
      </Column>
      <Column header="Spot">
        <template #body="slotProps">
          {{ Money(slotProps.data.form.spot) }}
        </template>
      </Column>
      <Column header="Strike">
        <template #body="slotProps">
          {{ Money(slotProps.data.form.strike) }}
        </template>
      </Column>
      <Column header="Premium">
        <template #body="slotProps">
          {{ Money(slotProps.data.form.premium) }}
        </template>
      </Column>
      <Column header="Break-even">
        <template #body="slotProps">
          {{ Money(slotProps.data.metrics.breakEven) }}
        </template>
      </Column>
      <Column header="Static Rate">
        <template #body="slotProps">
          {{ Percent(slotProps.data.metrics.staticRate) }}
        </template>
      </Column>
      <Column header="Actions" headerClass="p-text-center">
        <template #body="slotProps">
          <div class="p-text-center">
            <Button icon="pi pi-check" class="p-button-sm p-button-success p-mr-2" title="Load" @click="LoadCalculation(slotProps.data)" />
            <Button icon="pi pi-trash" class="p-button-sm p-button-danger" title="Delete" @click="DeleteCalculation(slotProps.data.id)" />
          </div>
        </template>
      </Column>
    </DataTable>
  </Dialog>
</template>

<script>
import Calendar from 'primevue/calendar'
import { ACTION, TYPE, simulate, buildOrderDraft, isTrackable, track, buildCloseOrderDraft } from '@/tools/OptionCalculator.js'

const NOT_AVAILABLE = 'n/a'
const percentFormat = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

function today () {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

export default {
  name: 'ModalOptionCalculator',
  components: { Calendar },
  props: {
    mode: {
      type: String,
      default: 'simulate'
    },
    order: {
      type: Object,
      default: null
    }
  },
  emits: ['fill-order', 'close-modal', 'save-calculation'],
  data () {
    return {
      display: true,
      showSavedDialog: false,
      savedCalculations: [],
      form: {
        symbolAggregate: null,
        symbol: null,
        type: TYPE.CALL,
        action: ACTION.SELL,
        spot: null,
        strike: null,
        premium: null,
        quantity: null,
        costs: 0,
        expirationDate: null,
        baseDate: today(),
        cdiPercent: null
      },
      tracking: {
        currentSpot: null,
        currentPremium: null,
        baseDate: today(),
        cdiPercent: null,
        openSpot: null
      },
      actions: [
        { name: 'Buy', value: ACTION.BUY },
        { name: 'Sell', value: ACTION.SELL }
      ],
      types: [
        { name: 'Call', value: TYPE.CALL },
        { name: 'Put', value: TYPE.PUT }
      ]
    }
  },
  mounted () {
    this.savedCalculations = this.GetSavedCalculations()
  },
  computed: {
    title () {
      return this.mode === 'track' ? 'Option Tracking' : 'Options Calculator'
    },
    savedCalculationsCount () {
      return this.savedCalculations.length
    },
    input () {
      const { cdiPercent, ...fields } = this.form
      return {
        ...fields,
        cdiAnnual: cdiPercent === null || cdiPercent === undefined ? null : cdiPercent / 100
      }
    },
    result () {
      return simulate(this.input)
    },
    isSellPut () {
      return this.form.action === ACTION.SELL && this.form.type === TYPE.PUT
    },
    metrics () {
      const r = this.result
      return [
        { label: 'Term', value: `${r.du} business days` },
        { label: this.form.action === ACTION.SELL ? 'Net Premium (Financial Volume)' : 'Net Financial Volume', value: this.Money(r.netVolume) },
        { label: 'Allocated Capital', value: this.Money(r.allocatedCapital) },
        { label: 'Static Rate (Unexercised)', value: this.Percent(r.staticRate) },
        { label: 'Exercise Rate', value: this.Percent(r.exerciseRate) },
        { label: 'Annualized Static Rate', value: this.Percent(r.staticRateAnnual) },
        { label: 'Annualized Exercise Rate', value: this.Percent(r.exerciseRateAnnual) },
        { label: 'Period CDI', value: this.Percent(r.cdiPeriod) },
        { label: 'Static Rate % of CDI', value: this.Percent(r.staticRateCdiPct) },
        { label: 'Exercise Rate % of CDI', value: this.Percent(r.exerciseRateCdiPct) },
        { label: this.isSellPut ? 'Break-even (Effective Purchase Price)' : 'Break-even', value: this.Money(r.breakEven) },
        { label: 'Max Loss', value: this.Money(r.maxLoss) },
        { label: 'Strike Distance', value: `${this.Percent(r.distance, true)} (${r.moneyness})` }
      ]
    },
    trackable () {
      return isTrackable(this.order)
    },
    trackInput () {
      const { cdiPercent, ...fields } = this.tracking
      return {
        ...fields,
        cdiAnnual: cdiPercent === null || cdiPercent === undefined ? null : cdiPercent / 100
      }
    },
    trackResult () {
      return track(this.order, this.trackInput)
    },
    canClose () {
      return this.trackable && this.trackResult.valid
    },
    progressPercent () {
      return Math.round(this.trackResult.progress * 100)
    },
    orderSummary () {
      const o = this.order
      return [
        { label: 'Option', value: `${o.symbol} (${o.symbolAggregate})` },
        { label: 'Action', value: o.action === ACTION.SELL ? 'Sell' : 'Buy' },
        { label: 'Type', value: o.type === TYPE.PUT ? 'Put' : 'Call' },
        { label: 'Quantity', value: o.quantity },
        { label: 'Opening Premium', value: this.Money(o.price) },
        { label: 'Strike Price', value: this.Money(o.strikePrice) },
        { label: 'Expiration Date', value: this.$formatDate(o.expirationDate) },
        { label: 'Opening Date', value: this.$formatDate(o.date) },
        { label: 'Opening Costs', value: this.Money((o.comission || 0) + (o.otherCost || 0)) }
      ]
    },
    trackMetrics () {
      const r = this.trackResult
      return [
        { label: 'P&L', value: this.Money(r.pnl) },
        { label: 'P&L (% of Premium)', value: this.Percent(r.pnlPct, true) },
        { label: 'Current Intrinsic Value', value: this.Money(r.intrinsic) },
        {
          label: 'Current Extrinsic Value',
          value: r.extrinsicNegative ? `${this.Money(r.extrinsic)} (negative: check entered spot and premium)` : this.Money(r.extrinsic),
          warn: r.extrinsicNegative
        },
        ...this.decayMetrics(r),
        { label: 'Business Days Elapsed', value: r.daysElapsed },
        { label: 'Business Days Remaining', value: r.expired ? '0 (Expired)' : r.daysRemaining },
        { label: 'Total Term (Business Days)', value: r.daysTotal },
        { label: 'Current Capital', value: this.Money(r.currentCapital) },
        { label: 'Remaining Rate', value: this.Percent(r.remainingRate) },
        { label: 'Annualized Remaining Rate', value: this.Percent(r.remainingRateAnnual) },
        { label: 'Remaining Rate % of CDI', value: this.Percent(r.remainingRateCdiPct) }
      ]
    }
  },
  methods: {
    SetUpperCase (field, value) {
      this.form[field] = value ? value.toUpperCase() : value
    },
    Money (value) {
      if (value === null || value === undefined) { return NOT_AVAILABLE }
      return this.$formatCurrency(value)
    },
    Percent (value, signed = false) {
      if (value === null || value === undefined) { return NOT_AVAILABLE }
      const text = `${percentFormat.format(value * 100)}%`
      return signed && value > 0 ? `+${text}` : text
    },
    decayMetrics (r) {
      if (r.extrinsicDecay === null) { return [] }
      return [
        { label: 'Opening Extrinsic Value', value: this.Money(r.openExtrinsic) },
        { label: 'Extrinsic Value Decay', value: this.Money(r.extrinsicDecay) }
      ]
    },
    GetSavedCalculations () {
      try {
        const raw = localStorage.getItem('ziontrade_saved_option_calculations')
        return raw ? JSON.parse(raw) : []
      } catch (e) {
        return []
      }
    },
    SaveCalculation () {
      if (!this.result.valid) { return }
      const calculation = {
        id: this.$guid ? this.$guid() : Date.now().toString(),
        savedAt: new Date().toISOString(),
        form: {
          symbolAggregate: this.form.symbolAggregate,
          symbol: this.form.symbol,
          type: this.form.type,
          action: this.form.action,
          spot: this.form.spot,
          strike: this.form.strike,
          premium: this.form.premium,
          quantity: this.form.quantity,
          costs: this.form.costs,
          expirationDate: this.form.expirationDate ? new Date(this.form.expirationDate).toISOString() : null,
          baseDate: this.form.baseDate ? new Date(this.form.baseDate).toISOString() : null,
          cdiPercent: this.form.cdiPercent
        },
        metrics: {
          du: this.result.du,
          netVolume: this.result.netVolume,
          allocatedCapital: this.result.allocatedCapital,
          staticRate: this.result.staticRate,
          exerciseRate: this.result.exerciseRate,
          staticRateAnnual: this.result.staticRateAnnual,
          exerciseRateAnnual: this.result.exerciseRateAnnual,
          cdiPeriod: this.result.cdiPeriod,
          staticRateCdiPct: this.result.staticRateCdiPct,
          exerciseRateCdiPct: this.result.exerciseRateCdiPct,
          breakEven: this.result.breakEven,
          maxLoss: this.result.maxLoss,
          distance: this.result.distance,
          moneyness: this.result.moneyness
        }
      }

      const saved = this.GetSavedCalculations()
      saved.unshift(calculation)
      if (saved.length > 50) { saved.length = 50 }
      localStorage.setItem('ziontrade_saved_option_calculations', JSON.stringify(saved))
      this.savedCalculations = saved

      this.$toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Calculation saved successfully',
        life: 3000
      })
      this.$emit('save-calculation', calculation)
    },
    LoadCalculation (calc) {
      if (!calc || !calc.form) { return }
      this.form.symbolAggregate = calc.form.symbolAggregate
      this.form.symbol = calc.form.symbol
      this.form.type = calc.form.type
      this.form.action = calc.form.action
      this.form.spot = calc.form.spot
      this.form.strike = calc.form.strike
      this.form.premium = calc.form.premium
      this.form.quantity = calc.form.quantity
      this.form.costs = calc.form.costs
      this.form.expirationDate = calc.form.expirationDate ? new Date(calc.form.expirationDate) : null
      this.form.baseDate = calc.form.baseDate ? new Date(calc.form.baseDate) : today()
      this.form.cdiPercent = calc.form.cdiPercent
      this.showSavedDialog = false
      this.$toast.add({
        severity: 'info',
        summary: 'Loaded',
        detail: 'Calculation loaded into form',
        life: 2500
      })
    },
    DeleteCalculation (id) {
      const saved = this.GetSavedCalculations().filter(c => c.id !== id)
      localStorage.setItem('ziontrade_saved_option_calculations', JSON.stringify(saved))
      this.savedCalculations = saved
      this.$toast.add({
        severity: 'warn',
        summary: 'Removed',
        detail: 'Saved calculation removed',
        life: 2500
      })
    },
    FillCloseOrder () {
      if (!this.canClose) { return }
      this.$emit('fill-order', buildCloseOrderDraft(this.order, this.trackInput))
    },
    FillOrder () {
      if (!this.result.valid) { return }
      this.$emit('fill-order', buildOrderDraft(this.input))
    },
    Cancel () {
      this.$emit('close-modal')
    },
    CloseModal () {
      this.$emit('close-modal')
    }
  }
}
</script>
